import { type ChangeEvent, type FormEvent, useRef, useState } from "react";
import {
  DISPLAY_NAME_MAX_LENGTH,
  deletePicture,
  isApiError,
  PICTURE_MAX_BYTES,
  type Profile,
  updateDisplayName,
  uploadPicture,
} from "../api.ts";
import { Avatar } from "../components/Avatar.tsx";
import { ConfirmDialog } from "../components/Dialog.tsx";
import { UploadIcon } from "../components/icons.tsx";
import { Card, LoadState, PageHeader, type Status, StatusMessage } from "../components/ui.tsx";
import { errorMessage } from "../errors.ts";
import type { LoadState as ProfileState } from "../use-loader.ts";

const ACCEPTED_TYPES = ["image/jpeg", "image/png"];

type Props = {
  profile: ProfileState<Profile>;
  onChange: (profile: Profile) => void;
  onRetry: () => void;
};

export function ProfilePage({ profile, onChange, onRetry }: Props) {
  return (
    <>
      <PageHeader
        title="基本設定"
        description="各プロダクトで表示されるアイコンと名前を設定します。"
      />
      <Card label="プロフィール">
        {profile.status === "loaded" ? (
          <ProfileForm profile={profile.data} onChange={onChange} />
        ) : (
          <LoadState state={profile.status} onRetry={onRetry} />
        )}
      </Card>
    </>
  );
}

function ProfileForm({ profile, onChange }: { profile: Profile; onChange: (p: Profile) => void }) {
  return (
    <>
      <PictureField profile={profile} onChange={onChange} />
      <NameForm profile={profile} onChange={onChange} />
    </>
  );
}

function PictureField({ profile, onChange }: { profile: Profile; onChange: (p: Profile) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>(null);
  const [confirming, setConfirming] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file === undefined) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setStatus({ tone: "error", text: "JPG か PNG の画像を選んでください。" });
      return;
    }
    if (file.size > PICTURE_MAX_BYTES) {
      setStatus({ tone: "error", text: "5 MB 以下の画像を選んでください。" });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      onChange(await uploadPicture(file));
      setStatus({ tone: "success", text: "アイコンを変更しました。" });
    } catch (error) {
      setStatus({ tone: "error", text: pictureErrorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    setDeleteError(null);
    try {
      onChange(await deletePicture());
      setConfirming(false);
      setStatus({ tone: "success", text: "アイコンを削除しました。" });
    } catch (error) {
      setDeleteError(errorMessage(error, "アイコンを削除できませんでした。"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="profile-picture">
      <Avatar
        name={profile.displayName ?? profile.email}
        picture={profile.picture}
        size="large"
        label="現在のアイコン"
      />
      <div className="profile-picture__body">
        <div className="field-label">アイコン</div>
        <div className="button-row">
          <button
            type="button"
            className="button button--secondary button--bold"
            onClick={() => input.current?.click()}
            disabled={busy}
          >
            <UploadIcon size={16} />
            <span>{busy ? "処理中…" : "画像をアップロード"}</span>
          </button>
          {profile.customPicture !== null ? (
            <button
              type="button"
              className="button button--danger"
              onClick={() => {
                setDeleteError(null);
                setConfirming(true);
              }}
              disabled={busy}
            >
              削除
            </button>
          ) : null}
          <input
            ref={input}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            className="visually-hidden"
            tabIndex={-1}
            aria-label="アイコンの画像を選ぶ"
            onChange={onFile}
          />
        </div>
        <p className="help">JPG・PNG 形式（5 MB まで）。正方形に切り抜いて表示します。</p>
        <StatusMessage status={status} />
      </div>
      {confirming ? (
        <ConfirmDialog
          title="アイコンを削除しますか？"
          confirmLabel="削除する"
          onConfirm={onDelete}
          onCancel={() => setConfirming(false)}
          busy={busy}
          error={deleteError}
        >
          <p className="dialog__text">
            削除すると、名前の頭文字（またはログインに使うサービスのアイコン）を表示します。
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

function pictureErrorMessage(error: unknown): string {
  if (isApiError(error, "IMAGE_TOO_LARGE"))
    return "画像が大きすぎます。5 MB・4096 × 4096 px 以下の画像を選んでください。";
  if (isApiError(error, "UNSUPPORTED_MEDIA_TYPE") || isApiError(error, "INVALID_IMAGE")) {
    return "画像を読み込めませんでした。JPG か PNG の画像を選んでください。";
  }
  return errorMessage(error, "アイコンを変更できませんでした。");
}

function NameForm({ profile, onChange }: { profile: Profile; onChange: (p: Profile) => void }) {
  const saved = profile.customDisplayName ?? profile.displayName ?? "";
  const [name, setName] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>(null);

  const trimmed = name.trim();
  const unchanged = trimmed === saved || trimmed === "";

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (unchanged || busy) return;
    setBusy(true);
    setStatus(null);
    try {
      const next = await updateDisplayName(trimmed);
      onChange(next);
      setName(next.customDisplayName ?? next.displayName ?? trimmed);
      setStatus({ tone: "success", text: "保存しました。" });
    } catch (error) {
      setStatus({
        tone: "error",
        text:
          isApiError(error) && error.status === 400
            ? "使えない文字が含まれています。記号や空白を見直してください。"
            : errorMessage(error, "保存できませんでした。"),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="profile-form" onSubmit={onSubmit} noValidate>
      <div className="field">
        <label htmlFor="display-name" className="field-label">
          名前（ニックネーム）
        </label>
        <input
          id="display-name"
          type="text"
          className="input"
          value={name}
          maxLength={DISPLAY_NAME_MAX_LENGTH}
          autoComplete="nickname"
          aria-describedby="display-name-help"
          onChange={(e) => {
            setName(e.target.value);
            setStatus(null);
          }}
        />
        <span id="display-name-help" className="help">
          各プロダクトのヘッダーなどに表示されます（{DISPLAY_NAME_MAX_LENGTH}{" "}
          文字まで）。各プロダクトには次回ログインしたときに反映されます。
        </span>
      </div>
      <div className="field">
        <div className="field-label">メールアドレス</div>
        <div className="field-value">{profile.email ?? "—"}</div>
        <span className="help">
          ログインに使用するため、ここでは変更できません。変更は担当コンサルタント・営業までご連絡ください。
        </span>
      </div>
      <div className="form-actions">
        <StatusMessage status={status} />
        <button type="submit" className="button button--primary" disabled={unchanged || busy}>
          {busy ? "保存中…" : "保存"}
        </button>
      </div>
    </form>
  );
}
